// src/oura/whatsnew/notes.ts
//
// What the "What's new" bar at the top of every staff Oura page announces
// (Jack, 4 Nov 2026). One short line and one link, nothing else: the page it
// links to carries the detail. Staff only; a merchant never sees it.
//
// To announce something: add an entry at the TOP with a new id, a line of at
// most ~90 characters, the link, and the last day it should show. The bar shows
// the newest entry that is still within its day and not closed by this person.
// Closing is remembered per id in this browser, so a new id shows again.

export interface WhatsNewNote {
    /** Stable, never reused: the dismiss key. */
    id: string;
    /** "New: Ask Oura answers plain questions and does small tasks." */
    line: string;
    /** Where "See what's new" goes. */
    href: string;
    /** Last day it shows, YYYY-MM-DD (Malaysia); gone the day after. */
    until: string;
}

export const WHATS_NEW: WhatsNewNote[] = [
    {
        id: "2026-11-04-ask-oura",
        line: "New: Ask Oura answers plain questions, lists what happened, and does small tasks when you say yes.",
        href: "https://claude.ai/artifact/T3PFf2R48xHArHPMkqtCv1",
        until: "2026-11-11",
    },
];

const KEY = (id: string) => `oura.whatsNew.closed.${id}`;

export function isClosed(id: string): boolean {
    try {
        return window.localStorage.getItem(KEY(id)) === "1";
    } catch {
        return false;
    }
}

export function close(id: string): void {
    try {
        window.localStorage.setItem(KEY(id), "1");
    } catch {
        // Private windows: it closes for this page view only.
    }
}

/** Today in Malaysia as YYYY-MM-DD, so "until" means the same day for everyone. */
function todayMY(): string {
    return new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kuala_Lumpur", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date());
}

/** The note to show now, or null. */
export function currentNote(now = todayMY()): WhatsNewNote | null {
    return WHATS_NEW.find((n) => n.until >= now && !isClosed(n.id)) ?? null;
}
